<template>
  <div class="v2026-chatnote">
    <!-- The head line always renders, even with no value — otherwise there is no way to add the
         first one. -->
    <div class="v2026-chatnote__section-head">
      <span class="v2026-chatnote__section-label">โน้ตแชท</span>
      <template v-if="customer">
        <button v-if="value" type="button" class="v2026-chatnote__icon-btn"
                @click="startEdit" aria-label="แก้ไขโน้ตแชท">
          <span v-if="registry.editIconMask" class="v2026-chatnote__mask v2026-chatnote__edit"
                aria-hidden="true" :style="maskStyle(registry.editIcon)"></span>
          <img v-else class="v2026-chatnote__edit-img" aria-hidden="true" :src="registry.editIcon" alt="">
        </button>
        <button v-else type="button" class="v2026-chatnote__add"
                @click="startEdit" aria-label="เพิ่มโน้ตแชท">
          <span class="v2026-chatnote__add-ico" aria-hidden="true" :style="maskStyle(plusIcon)"></span>
          <span class="v2026-chatnote__add-text">เพิ่ม</span>
        </button>
      </template>
    </div>

    <div v-if="value" class="v2026-chatnote__notebox">{{ value }}</div>

    <BaseModal :open="isEditing" max-width="700px" :position="['top', 'center']"
               :modalStyle="['margin-top:10%']" @close="isEditing = false">
      <div class="v2026-chatnote__editor">
        <div class="v2026-chatnote__editor-head">
          <span class="v2026-chatnote__editor-title">โน้ตแชท</span>
          <button type="button" class="v2026-chatnote__editor-close" aria-label="ปิด"
                  @click="isEditing = false">×</button>
        </div>
        <div class="v2026-chatnote__editor-body">
          <label class="v2026-chatnote__editor-label">โน้ตแชท</label>
          <div class="v2026-chatnote__editor-row">
            <div class="v2026-chatnote__editor-field">
              <textarea class="v2026-chatnote__editor-ta" :rows="registry.rows"
                        :maxlength="registry.maxlength" v-model="draft"
                        placeholder="พิมพ์ Note สำคัญ (ไม่เกิน 1 ประโยค)…"></textarea>
              <span v-if="registry.maxlength" class="v2026-chatnote__editor-counter">{{ (draft || '').length }}/{{ registry.maxlength }}</span>
            </div>
            <button type="button" class="v2026-chatnote__editor-save"
                    :disabled="saving" @click="save">
              {{ saving ? 'กำลังบันทึก…' : 'บันทึก' }}
            </button>
          </div>
          <p class="v2026-chatnote__editor-hint">* กรุณากรอกข้อความไม่เกิน 1 ประโยค เพื่อให้ Admin คนอื่นอ่านได้ง่าย</p>
          <div class="v2026-chatnote__editor-warn">
            <span class="v2026-chatnote__editor-warn-ico" aria-hidden="true">⚠️</span>
            Note สำคัญสั้นๆ เพื่อบอกสิ่งสำคัญ
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BaseModal from '@/components/coversation/BaseModal.vue'

// Per-panel differences, and the ONLY ones:
// 1. Pencil glyph. The customer panel's asset is stroke-based, so a CSS mask paints it as a
//    solid square — the two panels genuinely need different treatments here.
// 2. `maxlength: null` is deliberate (not undefined, not 0): Vue removes a null-bound
//    attribute, so the professional textarea carries no maxlength at all.
const TYPE_REGISTRY = {
  0: {
    name: 'customer',
    editIcon: require('@/assets/icons2026/pencil-edit.svg'),
    editIconMask: false,
    rows: 1,
    maxlength: null
  },
  1: {
    name: 'professional',
    editIcon: require('@/assets/icons2026/edit.svg'),
    editIconMask: true,
    rows: 4,
    maxlength: null
  }
}

export default {
  name: 'chat-note-v2026',
  components: {
    BaseModal
  },
  props: {
    type: {
      type: Number,
      default: 0
    },
    customer: {
      type: Object,
      default: null
    },
    conversationId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      isEditing: false, // edit BaseModal visibility
      draft: '', // edit-mode textarea buffer
      // saveCustomer does not commit to the store, so the just-saved text is held here.
      override: undefined,
      saving: false, // PUT in flight → disable save
      plusIcon: require('@/assets/icons2026/plus.svg')
    }
  },
  computed: {
    registry () {
      return TYPE_REGISTRY[this.type] || TYPE_REGISTRY[0]
    },
    value () {
      if (this.override !== undefined) return this.override
      return this.customer ? this.customer.note : null
    }
  },
  watch: {
    // Drop the local override on a conversation change, or the previous chat's note leaks through.
    conversationId () {
      this.override = undefined
      this.isEditing = false
    }
  },
  methods: {
    ...mapActions('customer', ['saveCustomer']),
    startEdit () {
      this.draft = this.value || ''
      this.isEditing = true
    },
    // Empty is allowed — that IS the delete path. Sent UNTRIMMED, unlike ImportantMessageV2026.
    async save () {
      if (!this.customer || !this.customer.id) return
      this.saving = true
      try {
        await this.saveCustomer({
          customer: { id: this.customer.id, note: this.draft },
          conversation_id: this.conversationId
        })
        this.override = this.draft
        // A property write, not a prop reassignment, so it is legal in Vue 2. saveCustomer does
        // not commit to Vuex, so this is the only way other consumers see the new value.
        this.customer.note = this.draft
        this.isEditing = false
        this.$emit('notify', { variant: 'success', title: 'บันทึกข้อมูลสำเร็จ', message: 'ทำการบันทึกข้อมูลสำเร็จ' })
      } catch (e) {
        // Keep the modal open on failure so the draft is not lost.
        this.$emit('notify', { variant: 'danger', title: 'เกิดข้อผิดพลาด', message: 'บันทึกไม่สำเร็จ กรุณาลองใหม่' })
      } finally {
        this.saving = false
      }
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-chatnote {
  --v2026-chatnote-text: #101828;
  --v2026-chatnote-text-secondary: #667085;
  --v2026-chatnote-surface-sunken: #F8FAFC;
  --v2026-chatnote-surface-hover: #e9ecf1;
  --v2026-chatnote-icon: #475467;
  --v2026-chatnote-main: #31C1D7;

  display: flex;
  flex-direction: column;
  gap: 8px;
}

.v2026-chatnote__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.v2026-chatnote__section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--v2026-chatnote-text-secondary);
}

.v2026-chatnote__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-chatnote__icon-btn:hover {
  background: var(--v2026-chatnote-surface-hover);
}
.v2026-chatnote__icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-chatnote__mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-chatnote-icon);
}
.v2026-chatnote__edit {
  width: 16px;
  height: 16px;
  background-color: var(--v2026-chatnote-icon);
}

.v2026-chatnote__edit-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
}

.v2026-chatnote__add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px 0 6px;
  border: 0;
  background: transparent;
  border-radius: 999px;
  color: var(--v2026-chatnote-main);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.v2026-chatnote__add:hover {
  background: rgba(49, 193, 215, 0.12);
}
.v2026-chatnote__add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-chatnote__add-ico {
  display: inline-block;
  width: 14px;
  height: 14px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-chatnote__notebox {
  margin-top: 8px;
  min-height: 70px;
  background: var(--v2026-chatnote-surface-sunken);
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 700;
  color: var(--v2026-chatnote-text);
  font-size: 14px;
  line-height: 1.5;

  white-space: pre-line;
}

.v2026-chatnote__editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  background: #FFFFFF;
  border-bottom: 1px solid #EBEDF0;
  padding: 16px 24px;
}
.v2026-chatnote__editor-body {
  padding: 20px 24px;
}
.v2026-chatnote__editor-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--v2026-chatnote-text);
}
.v2026-chatnote__editor-close {
  border: none;
  background: none;
  padding: 0;
  font-size: 20px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-chatnote__editor-label {
  display: block;
  margin: 0 0 6px;
  font-size: 14px;
  color: #66696C;
}
.v2026-chatnote__editor-row {
  display: flex;
  gap: 12px;
  align-items: stretch;
}
.v2026-chatnote__editor-field {
  position: relative;
  flex: 1;
}
.v2026-chatnote__editor-ta {
  width: 100%;
  min-height: 44px;
  resize: none;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 10px 56px 10px 12px;
  font-size: 14px;
}
.v2026-chatnote__editor-ta:focus {
  outline: none;
  border-color: var(--v2026-chatnote-main);
}
.v2026-chatnote__editor-counter {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 13px;
  color: #9CA3AF;
  pointer-events: none;
}
.v2026-chatnote__editor-save {
  height: 44px;
  padding: 0 20px;
  background: var(--v2026-chatnote-main);
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.v2026-chatnote__editor-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.v2026-chatnote__editor-hint {
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 12px;
  color: #9CA3AF;
}
.v2026-chatnote__editor-warn {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FEF9C3;
  color: #92400E;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
}
</style>
